import { useNavigate } from 'react-router-dom';

export default function ProgramsSection() {
  const navigate = useNavigate();

  const programs = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Wealth Mastery Charter',
      subtitle: 'Economic stewardship, enterprise development, financial dominion',
      description: 'Master the principles of wealth creation and financial stewardship through our comprehensive charter program.',
      image: 'https://public.readdy.ai/ai/img_res/99021474fe5bfb644bffdedde1bd5ffa.jpg',
      features: ['Founders Forum', 'Entrepreneurs Forum', 'Investors Forum'],
      gradient: 'from-emerald-500 to-teal-600'
    },
    {
      icon: 'ri-heart-line',
      title: 'Relationship Wholeness Charter',
      subtitle: 'Emotional intelligence, covenantal commitment, relational healing',
      description: 'Build authentic connections and heal relationships through our transformational relationship programs.',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20diverse%20couple%20of%20color%20embracing%20lovingly%20in%20golden%20sunset%20light%2C%20warm%20intimate%20atmosphere%2C%20elegant%20relationship%20and%20emotional%20connection%2C%20peaceful%20garden%20setting%20with%20soft%20bokeh%20background%2C%20genuine%20love%20and%20partnership%20aesthetic%2C%20African%20American%20or%20Latino%20couple%2C%20no%20white%20people&width=600&height=400&seq=relationship-charter-diverse&orientation=landscape',
      features: ['Singles Forum', 'Married Forum', 'Post-Marriage Forum'],
      gradient: 'from-rose-500 to-pink-600'
    },
    {
      icon: 'ri-parent-line',
      title: 'Generational Legacy Charter',
      subtitle: 'Mentorship, cultural influence, heritage transfer, identity building',
      description: 'Create lasting impact that transcends generations through intentional legacy building and mentorship.',
      image: 'https://readdy.ai/api/search-image?query=Three%20generations%20of%20African%20American%20family%20together%2C%20grandfather%20father%20and%20child%20sharing%20wisdom%2C%20warm%20golden%20lighting%2C%20elegant%20home%20setting%2C%20legacy%20and%20heritage%20transfer%20moment%2C%20loving%20mentorship%20and%20family%20bonding%2C%20sophisticated%20intergenerational%20connection&width=600&height=400&seq=legacy-charter-img&orientation=landscape',
      features: ['Fathers Forum', 'Youth Forum', 'Showbiz & Influence Forum'],
      gradient: 'from-purple-500 to-indigo-600'
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden" style={{ background: '#3D2C8D' }}>
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-amber-400/10 to-yellow-300/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-amber-500/10 to-yellow-400/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-1/3 w-40 h-40 bg-gradient-to-r from-amber-300/10 to-yellow-200/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
            Charter-Based
            <span className="block text-5xl lg:text-6xl bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
              Programs
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full mb-8"></div>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed" style={{ color: '#FFFFFF' }}>
            Three primary pillars designed for comprehensive transformation in wealth, relationships, and generational legacy.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {programs.map((program, index) => (
            <div 
              key={index}
              className="group bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-amber-400/20 hover:bg-white/10 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={program.image}
                  alt={program.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${program.gradient} rounded-xl flex items-center justify-center`}>
                    <i className={`${program.icon} text-2xl text-white`}></i>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                  {program.title}
                </h3>
                <p className="text-sm mb-4 font-medium" style={{ color: '#F5A623' }}>
                  {program.subtitle}
                </p>
                <p className="mb-6 leading-relaxed" style={{ color: '#FFFFFF' }}>
                  {program.description}
                </p>

                {/* Features */}
                <div className="space-y-2 mb-8">
                  {program.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center">
                      <i className="ri-check-line mr-3 text-amber-400"></i>
                      <span className="text-sm text-white/90">{feature}</span>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={() => navigate('/charters')}
                  className={`w-full py-3 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r ${program.gradient} hover:opacity-90 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1`}
                >
                  Explore Charter
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-amber-600 to-yellow-500 rounded-3xl p-12 text-white max-w-4xl mx-auto">
            <h3 className="text-3xl lg:text-4xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
              Ready to Transform Your Life?
            </h3>
            <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
              Join thousands of leaders who are mastering wealth, healing relationships, and building generational legacy through The CROWN Abundance Movement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/membership')}
                className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <i className="ri-crown-line mr-2"></i>
                View Membership
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="border-2 border-white text-white hover:bg-white hover:text-amber-600 px-8 py-4 rounded-lg font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-line mr-2"></i>
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}